import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { EntityPickerBase } from '../../../../shared/components/common';
import { useOrganization, useOrganizations } from '../hooks/useOrganizations';
import { OrganizationQuickCreateDialog } from './OrganizationQuickCreateDialog';
import { getEntityUrl } from '../../../../shared/utils/entityUrls';
import type { Organization } from '../types';

interface OrganizationPickerProps {
  /** Currently selected company ID */
  value: string;
  /** Callback when a company is selected */
  onChange: (organizationId: string) => void;
  /** Callback returning the selected company object (for auto-populating name) */
  onCompanySelected?: (company: Organization | null) => void;
  /** Filter companies by type (e.g., 'vendor', 'customer') */
  organizationRelationshipFilter?: string;
  /** Optional label to display above the picker */
  label?: string;
  /** Placeholder text when no company is selected */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the "None" option to clear selection */
  allowClear?: boolean;
  /** Label for the "Add new" link (e.g., "Add new supplier") */
  addNewLabel?: string;
  /** Default relationships for the quick create dialog */
  defaultRelationships?: string[];
  /** Hide the "Add new" action (useful in filter contexts) */
  hideAddNew?: boolean;
  /** Show selected company as a clickable link to its detail page */
  linkToEntity?: boolean;
  /** Pre-populate search when popover opens (e.g., cleaned transaction description) */
  initialSearch?: string;
  /** Additional CSS classes for the popover (e.g., min-width override) */
  popoverClassName?: string;
}

export function OrganizationPicker({
  value,
  onChange,
  onCompanySelected,
  organizationRelationshipFilter,
  label,
  placeholder,
  disabled,
  className,
  allowClear = false,
  addNewLabel,
  defaultRelationships,
  hideAddNew = false,
  linkToEntity = false,
  initialSearch,
  popoverClassName,
}: OrganizationPickerProps) {
  const { t } = useTranslation('contacts');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const prevOpenRef = useRef(false);

  // Pre-populate search when popover opens
  useEffect(() => {
    if (open && !prevOpenRef.current && initialSearch) {
      setSearch(initialSearch);
    }
    prevOpenRef.current = open;
  }, [open, initialSearch]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogDomain, setCreateDialogDomain] = useState('');

  const { data: companiesData } = useOrganizations({
    organization_relationship: organizationRelationshipFilter,
    search: search || undefined,
    limit: 50,
  });

  const companies = useMemo(() => companiesData?.data ?? [], [companiesData?.data]);

  // The selected org can fall outside the relationship-filtered list (e.g. a
  // drop-ship PO's destination customer with no 'customer' relationship tag).
  // A picker must always render its selected value's label, so fetch the org
  // by id when the loaded list doesn't contain it and pin it to the options
  // (IMP-2). Waiting for the list avoids a spurious by-id fetch on mount.
  const selectedInList = !value || companies.some((c) => c.id === value);
  const { data: selectedOrg } = useOrganization(
    companiesData === undefined || selectedInList ? '' : value
  );
  const items = useMemo(
    () =>
      !selectedInList && !search && selectedOrg
        ? [selectedOrg, ...companies]
        : companies,
    [companies, selectedInList, search, selectedOrg]
  );

  const handleSelect = useCallback((organizationId: string) => {
    const company = items.find((c) => c.id === organizationId) ?? null;
    onChange(organizationId);
    onCompanySelected?.(company);
    setOpen(false);
    setSearch('');
  }, [items, onChange, onCompanySelected]);

  const handleClear = useCallback(() => {
    onChange('');
    onCompanySelected?.(null);
    setOpen(false);
    setSearch('');
  }, [onChange, onCompanySelected]);

  const [createDialogName, setCreateDialogName] = useState('');

  const handleAddNew = useCallback(() => {
    const searchText = search.trim();
    // If search text looks like a domain (contains a dot), use it as domain; otherwise as name
    if (searchText.includes('.')) {
      setCreateDialogDomain(searchText);
      setCreateDialogName('');
    } else {
      setCreateDialogDomain('');
      setCreateDialogName(searchText);
    }
    setOpen(false);
    setShowCreateDialog(true);
  }, [search]);

  const handleCompanyCreated = useCallback((company: Organization) => {
    onChange(company.id);
    onCompanySelected?.(company);
    setSearch('');
    setShowCreateDialog(false);
  }, [onChange, onCompanySelected]);

  const resolvedAddNewLabel = addNewLabel ?? (search.trim()
    ? t('companyPicker.createCompanyWithName', { name: search.trim() })
    : t('companyPicker.addNewCompany'));

  return (
    <>
      <EntityPickerBase<Organization>
        value={value}
        items={items}
        getItemLabel={(c) => c.name}
        renderItem={(company) => (
          <>
            <Building2 className="me-1.5 h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate">{company.name}</span>
            {company.domain && (
              <span className="ms-1 text-xs text-muted-foreground shrink-0">
                {company.domain}
              </span>
            )}
          </>
        )}
        label={label}
        placeholder={placeholder ?? t('companyPicker.searchOrCreate')}
        searchPlaceholder={t('companyPicker.searchCompanies')}
        emptyMessage={t('companyPicker.noCompaniesFound')}
        disabled={disabled}
        className={className}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={setOpen}
        onSelect={handleSelect}
        allowClear={allowClear}
        onClear={handleClear}
        hideAddNew={hideAddNew}
        addNewLabel={resolvedAddNewLabel}
        onAddNew={handleAddNew}
        selectedEntityUrl={linkToEntity && value
          ? getEntityUrl({ entityType: 'companies', entityId: value })
          : undefined}
        entityIcon={linkToEntity ? Building2 : undefined}
        popoverClassName={popoverClassName}
      />

      <OrganizationQuickCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handleCompanyCreated}
        skipNavigation
        defaultRelationships={defaultRelationships}
        defaultLookup={createDialogDomain}
        defaultName={createDialogName}
      />
    </>
  );
}
